import {
    consoleLogger,
    CoreDI, DI,
    EventDataType,
    IBrowser,
    IEventManagerWithAsync, ILogic,
    inject,
    injectable,
    ModuleIsEnabledFilter
} from '@gongxigames/framework/slot';
import {ILightAndWonderMessageChannel} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderInterface';
import {
    Game2GCMClientSideMessagesMap,
    GCM2GameClientSideMessagesMap, LAWRegulationSettings,
    LightAndWonderEventsMap
} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderType';
import {LightAndWonderIntegrationDI} from '@gongxigames/framework/integrations/lightAndWonder/LightAndWonderIntegrationDI';
import {isEnabledLightAndWonder} from '@gongxigames/framework/integrations/lightAndWonder/isEnabledLightAndWonder';
import {
    RubyPlayGameClientWrapperSettings
} from '@gongxigames/framework/integrations/rubyplay/RubyPlayGameClientWrapperSettings';

@injectable()
export class LightAndWonderMessageChannel implements ILightAndWonderMessageChannel {
    public static readonly NAME = LightAndWonderIntegrationDI.MessageChannel;
    public static readonly isEnabled: ModuleIsEnabledFilter = isEnabledLightAndWonder;

    protected port1!: MessagePort;
    protected port2!: MessagePort;
    protected isGameReady = false;
    protected shouldEmitMessageFromGCM = false;

    constructor(
        @inject(CoreDI.IEventManagerWithAsync) private eventManager: IEventManagerWithAsync,
        @inject(CoreDI.Browser) private browser: IBrowser,
        @inject(DI.Logic) private logic: ILogic,
        @inject(CoreDI.Settings) private settings: RubyPlayGameClientWrapperSettings
    ) {
        const channel = this.browser.createMessageChanel();

        this.port1 = channel.port1;
        this.port2 = channel.port2;

        const pmInitObj = JSON.stringify({eventName: 'com.lnw.gcm.initialiseChannel'});

        this.browser.postMessageToParent(pmInitObj, '*', [this.port2]);
        this.port1.onmessage = this.onMessage.bind(this);
        this.eventManager.emit('General.Game.ENABLE_FIRST_INTERACTION', false);
    }

    private onMessage(msg: any) {
        const {eventName, eventData} = JSON.parse(msg.data);

        this.getMsgFromGCM(eventName, eventData);
    };

    public getMsgFromGCM<K extends keyof GCM2GameClientSideMessagesMap>(eventName: K, ...eventData: EventDataType<LightAndWonderEventsMap[K]>): void {
        consoleLogger.log('getMsgFromGSM: ', eventName, eventData);

        if (!this.shouldEmitMessageFromGCM) {
            this.eventManager.emit(eventName, ...eventData);
        }
    }

    public sendMsgToGCM<K extends keyof Game2GCMClientSideMessagesMap>(eventName: K, eventData?: Game2GCMClientSideMessagesMap[K]): void {
        consoleLogger.log('sendMsgToGCM: ', eventName, eventData);
        this.port1.postMessage(JSON.stringify({
            eventName,
            eventData
        }));
    }

    public gameReadyMessage(shouldEmitMessageFromGCM = true): void {
        if (!this.isGameReady) {
            this.shouldEmitMessageFromGCM = shouldEmitMessageFromGCM;
            this.isGameReady = true;
            this.sendMsgToGCM('com.lnw.gcm.gameReady', {settings: this.getRegulationSettings()});
        }
    }

    private getRegulationSettings(): LAWRegulationSettings | undefined {
        try {
            const nonMinimalBetPopupEnabled: boolean = this.settings.get('nonMinimalBetPopupEnabled');
            const subunit = this.settings.get('subunit');
            const initialBet = this.logic.getTotalBet() / subunit;
            const minBet = this.logic.getBets()[0] * this.logic.getWager() / subunit;

            return nonMinimalBetPopupEnabled ?
                {
                    regulations: ['nl_min_bet'],
                    betSettings: {
                        initialBet,
                        minBet
                    }
                }
                :
                undefined;
        } catch (error) {
            return undefined;
        }
    }
}